import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import { OrderTypeToColor } from './OrgDashboard';
import { getPercentage } from '../../../helpers/global';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export default function BarGraph({
  chartData,
  countByOrderType,
  interval = 'monthly',
  isStacked = false,
  themeColor = '',
  showTotalPercentage = true
}) {
  const hasCountByOrderType = countByOrderType !== null ? true : false;
  const formatLabel = (startDate, endDate, interval) => {
    const start = moment(startDate);
    const end = moment(endDate);

    switch (interval) {
      case 'daily':
        return start.format('MMM D');
      case 'weekly':
        return `${start.format('MMM D')} - ${end.format('MMM D')}`;
      case 'monthly':
        return `${start.format('MMM D')} - ${end.format('MMM D')}`;
      case 'yearly':
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      default:
        return start.format('MMM D');
    }
  };

  // Calculate total counts grouped by order_type
  const calculateGroupedTotals = () => {
    const totals = {};

    chartData.forEach(item => {
      if (item.group && Array.isArray(item.group)) {
        // For stacked charts with group data
        item.group.forEach(groupItem => {
          const orderType = groupItem._id;
          totals[orderType] = (totals[orderType] || 0) + groupItem.count;
        });
      } else {
        // For regular charts, assume it's a single order type
        const orderType = 'Orders';
        totals[orderType] = (totals[orderType] || 0) + item.count;
      }
    });

    return totals;
  };

  // Calculate overall total
  const calculateTotal = () => {
    let total = 0;
    chartData.forEach(item => {
      if (item.group && Array.isArray(item.group)) {
        // For stacked charts with group data
        item.group.forEach(groupItem => {
          total += groupItem.count;
        });
      } else {
        // For regular charts
        total += item.count;
      }
    });
    return total;
  };

  const groupedTotals = calculateGroupedTotals();
  const totalCount = calculateTotal();

  // Check if data has group structure (for stacked bars)
  const hasGroupData = chartData[0] && chartData[0].group && Array.isArray(chartData[0].group);

  let data;
  let options;

  if (hasGroupData && isStacked) {
    // Handle stacked bar chart with grouped data
    const allServiceTypes = [...new Set(chartData.flatMap(item => item.group.map(g => g._id)))];

    data = {
      labels: chartData.map(item => formatLabel(item.startDate, item.endDate, interval)),
      datasets: allServiceTypes.map((serviceType, idx) => ({
        label: serviceType,
        data: chartData.map(item => {
          const groupItem = item.group.find(g => g._id === serviceType);
          return groupItem ? groupItem.count : 0;
        }),
        backgroundColor: OrderTypeToColor[serviceType]
      }))
    };

    options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 20
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          cornerRadius: 6,
          padding: 8,
          callbacks: {
            title: function(context) {
              return context[0].label;
            },
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}`;
            }
          }
        },
        datalabels: {
          display: function(context) {
            if (isStacked) {
              const dataIndex = context.dataIndex;
              const barData = chartData[dataIndex];
              if (barData && barData.group) {
                // Check if this is the topmost dataset with data
                const datasets = data.datasets;
                for (let i = datasets.length - 1; i >= 0; i--) {
                  if (datasets[i].data[dataIndex] > 0) {
                    return context.datasetIndex === i;
                  }
                }
                return false; // No data in any dataset
              }
              return context.datasetIndex === 0; // Fallback to first dataset
            }
            return true;
          },
          color: '#000',
          anchor: 'end',
          align: 'top',
          offset: 1,
          font: {
            weight: 'normal',
            size: 12
          },
          formatter: function(value, context) {
            if (isStacked) {
              const dataIndex = context.dataIndex;
              const barData = chartData[dataIndex];
              if (barData && barData.group) {
                const total = barData.group.reduce((sum, groupItem) => sum + groupItem.count, 0);
                return total > 0 ? total : null;
              }
            }
            return value > 0 ? value : null;
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: {
              size: 11
            }
          }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      interaction: {
        intersect: false,
        mode: 'index'
      }
    };
  } else {
    // Handle regular bar chart (for services stats)
    data = {
      labels: chartData.map(item => formatLabel(item.startDate, item.endDate, interval)),
      datasets: [
        {
          label: 'Orders',
          data: chartData.map(item => item.count),
          backgroundColor: '#059669',
          borderWidth: 2
        }
      ]
    };

    options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          cornerRadius: 6,
          padding: 8,
          callbacks: {
            title: function(context) {
              return context[0].label;
            },
            label: function(context) {
              return `Orders: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: {
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      interaction: {
        intersect: false,
        mode: 'index'
      }
    };
  }

  return (
    <div
      className="card border-0 h-100 rounded"
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}
    >
      <div className="card-body">
        {/* Grouped totals section - moved to top */}
        <div className="d-flex flex-row align-items-center">
          <div className="mb-4 pb-3">
            <div className="d-flex flex-wrap gap-4 justify-content-center">
              <div
                className="text-muted me-2 "
                style={{
                  fontWeight: '600',
                  fontSize: '12px',
                  letterSpacing: '0.3px',
                  marginRight: '20px',
                  border: `1px solid ${themeColor}`,
                  padding: '4px 6px'
                }}
              >
                <div className="text-black d-flex align-items-center">
                  <span className="mr-1" style={{ color: '#4a5568', letterSpacing: '0.3px', marginRight: '4px' }}>
                    Total:
                  </span>
                  {hasCountByOrderType && showTotalPercentage ? (
                    <div
                      className="d-flex align-items-center"
                      style={{
                        color: '#1a202c',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      <span className="mr-2">{totalCount}</span>
                      <span>({getPercentage(totalCount, countByOrderType.total)})</span>
                    </div>
                  ) : (
                    <span style={{ color: '#1a202c', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}> {totalCount}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {Object.keys(groupedTotals).length > 0 && (
            <div className="mb-4 pb-3 border-bottom" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div className="d-flex flex-wrap gap-4 justify-content-center">
                {Object.entries(groupedTotals).map(([orderType, count], index) => (
                  <React.Fragment key={orderType}>
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle"
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: OrderTypeToColor[orderType] || '#6B7280',
                          boxShadow: `0 2px 8px ${OrderTypeToColor[orderType] || '#6B7280'}40`,
                          marginRight: '4px'
                        }}
                      />
                      <span
                        className="text-muted"
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#4a5568',
                          letterSpacing: '0.3px',
                          marginRight: '4px'
                        }}
                      >
                        {orderType}:
                      </span>
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: '12px',
                          color: '#1a202c',
                          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                      >
                        {hasCountByOrderType ? (
                          <div className="d-flex align-items-center">
                            <span className="mr-2">{count}</span>
                            <span>
                              ({getPercentage(count, showTotalPercentage ? countByOrderType[orderType] : totalCount)})
                            </span>
                          </div>
                        ) : (
                          count.toLocaleString()
                        )}
                      </div>
                    </div>
                    {index < Object.entries(groupedTotals).length - 1 && (
                      <span
                        className="text-muted"
                        style={{
                          fontSize: '0.85rem',
                          margin: '0 8px',
                          opacity: 0.6
                        }}
                      >
                        |
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ height: '300px' }}>
          <Bar data={data} options={options} />
        </div>
      </div>
    </div>
  );
}
